import { X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'
import { useT } from '@/shared/i18n'
import { Portal } from './Portal'

export interface SheetProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  footer?: ReactNode
  className?: string
}

/**
 * Panel lateral. Se abre SIN cambiar de ruta: la lista de atras queda montada,
 * con su scroll y su cache intactos, y cerrar no dispara una navegacion.
 */
export function Sheet({ open, onClose, title, children, footer, className }: SheetProps) {
  const t = useT()
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    panel.current?.focus()

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex justify-end">
        <button
          type="button"
          aria-label={t('common.ui.closePanel')}
          onClick={onClose}
          className="absolute inset-0 bg-bg/60"
        />
        <div
          ref={panel}
          role="dialog"
          aria-modal="true"
          aria-label={typeof title === 'string' ? title : t('common.ui.detail')}
          tabIndex={-1}
          className={cn(
            'relative flex h-full w-full max-w-[420px] flex-col border-l border-hairline bg-surface-1',
            className,
          )}
        >
          <header className="hairline-b flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">{title}</div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t('common.ui.closeDialog')}
              className="shrink-0 rounded-[var(--radius-control)] p-1 text-text-3 hover:text-text-1"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto px-4 py-3">{children}</div>

          {footer ? <footer className="hairline-t px-4 py-3">{footer}</footer> : null}
        </div>
      </div>
    </Portal>
  )
}
